import React, { useEffect, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";

// import "./ToolbarSystem.css";
import chevronDown from "./icons/ChevronDown.svg";
import chevronUp from "./icons/ChevronUp.svg";
import positionIcon from "./icons/Position.svg";
import heartbeatIcon from "./icons/HeartbeatIcon.svg";
import platformRadar from "./icons/PlatformRadar.svg";
import openNewWindowIcon from "./icons/OpenNewWindowIcon.svg";
import zendeskIcon from "./icons/Zendesk.svg";
import confluenceIcon from "./icons/Confluence.svg";
import phoneIcon from "./icons/Phone.svg";
import gameInProgressIcon from "./icons/GameInProgressIcon.svg";

import iconLibrary from "assets/images/StatusIcons/iconLibrary";
import { MarkerSeverity } from "../../enums";
import { ISystemData } from "../../interfaces";

interface IToolbarSystemProps {
  markerData: ISystemData;
  selectHandler: (system: ISystemData | null) => void;
  panTo: Function;
  selected: boolean;
  lastSelectedRef: React.MutableRefObject<HTMLDivElement | null>;
}

function ToolbarSystem({
  markerData,
  selectHandler,
  panTo,
  selected,
  lastSelectedRef,
}: IToolbarSystemProps) {
  const [copySuccess, setCopySuccess] = useState("Copy");

  const getTimeDifferenceFromNowInMin = (timeA: Date): string => {
    const nowDateNumber: Date = new Date();
    const diffInMin: number = Math.floor(
      Math.abs(timeA.getTime() - nowDateNumber.getTime()) / 1000 / 60
    );
    if (diffInMin === 0) return "less then 1m ago";

    let timeString = "";

    if (Math.floor(diffInMin / 60) > 0)
      timeString += `${Math.floor(diffInMin / 60)}h `;

    if (diffInMin % 60 > 0) timeString += `${diffInMin % 60}m `;

    timeString += "ago";
    return timeString;
  };

  const gameInProgressText: string = "Game in Progress";
  const defaultCityText: string = "";
  const defaultStateText: string = "";
  const defaultLocationText: string = "Location?";
  const cityText: string = markerData.position.city
    ? markerData.position.city
    : defaultCityText;
  const stateText: string = markerData.position.state
    ? markerData.position.state
    : defaultStateText;
  const locationText: string =
    cityText || stateText
      ? cityText && stateText
        ? cityText + ", " + stateText
        : cityText + stateText
      : defaultLocationText;

  useEffect(() => {
    if (copySuccess !== "Copy") {
      setTimeout(() => {
        setCopySuccess("Copy");
      }, 2000);
    }
  }, [copySuccess]);

  const panToMarker = () => {
    panTo({
      lat: markerData.position.lat,
      lng: markerData.position.lng,
      zoom: 17,
    });
  };

  const isSystemTypeContainingErrors = (
    systemType: MarkerSeverity
  ): boolean => {
    switch (systemType) {
      case MarkerSeverity.Critical:
      case MarkerSeverity.Warning:
        return true;
      case MarkerSeverity.Normal:
      case MarkerSeverity.Unknown:
      default:
        return false;
    }
  };

  const openLink = (link: string | undefined | null) => {
    if (link) window.open(link, "_blank");
    else
      window.open(
        "https://www.google.com/search?q=" + markerData.name,
        "_blank"
      );
  };

  const getTitleIcon = (statusCode: string) => {
    switch (statusCode) {
      case MarkerSeverity.Normal:
        return (
          <img
            src={iconLibrary.severityIcon.normal}
            alt=""
            className="unit-title-icon"
          />
        );
      case MarkerSeverity.Critical:
        return (
          <img
            src={iconLibrary.severityIcon.error}
            alt=""
            className="unit-title-icon"
          />
        );
      case MarkerSeverity.Warning:
        return (
          <img
            src={iconLibrary.severityIcon.warning}
            alt=""
            className="unit-title-icon"
          />
        );
      default:
        return (
          <img
            src={iconLibrary.severityIcon.unknown}
            alt=""
            className="unit-title-icon"
          />
        );
    }
  };

  return (
    <div
      className={selected ? "unit-selected" : "unit"}
      ref={selected ? lastSelectedRef : null}
      style={
        selected && !isSystemTypeContainingErrors(markerData.status.statusType)
          ? { height: "170px" }
          : {}
      }
    >
      <div className="unit-title">
        {markerData.status.statusType === MarkerSeverity.Critical
          ? getTitleIcon(MarkerSeverity.Critical)
          : markerData.status.statusType === MarkerSeverity.Warning
          ? getTitleIcon(MarkerSeverity.Warning)
          : markerData.status.statusType === MarkerSeverity.Normal
          ? getTitleIcon(MarkerSeverity.Normal)
          : getTitleIcon(MarkerSeverity.Unknown)}
        <span className="unit-title-text">{markerData.name}</span>
      </div>

      <div className="unit-info">
        <p>
          <img
            src={positionIcon}
            alt="Location"
            onClick={panToMarker}
            style={{ paddingLeft: "0px" }}
          />
          {locationText}
          {markerData.status.heartbeat && (
            <>
              <img src={heartbeatIcon} alt="Hearthbeat" />
              {getTimeDifferenceFromNowInMin(markerData.status.heartbeat)}
            </>
          )}
          {markerData.status.gameInProgress && (
            <>
              <img src={gameInProgressIcon} alt="GameInProgress" />
              {gameInProgressText}
            </>
          )}
        </p>
      </div>
      <div
        className="unit-unfolding-icon"
        onClick={() => {
          selected ? selectHandler(null) : selectHandler(markerData);
        }}
      >
        <img src={selected ? chevronUp : chevronDown} alt="" />
      </div>
      {selected && (
        <>
          <div
            className="unit-error-messages"
            style={
              isSystemTypeContainingErrors(markerData.status.statusType)
                ? {}
                : { height: "0px", padding: "0px" }
            }
          >
            {isSystemTypeContainingErrors(markerData.status.statusType) && (
              <>
                <div className="unit-error-copy">
                  <p>{copySuccess}</p>
                  <CopyToClipboard
                    text={
                      markerData.status.errors.length >= 1
                        ? markerData.status.errors.join("\n")
                        : ""
                    }
                    onCopy={() => {
                      setCopySuccess("Copied Successfully!");
                    }}
                  >
                    <img src={iconLibrary.duplicate} alt="" />
                  </CopyToClipboard>
                </div>
                <span>
                  <p
                    style={{
                      color: "#A0A0A0",
                      fontFamily: "Lato",
                      fontStyle: "normal",
                      fontWeight: "normal",
                      fontSize: "11px",
                      lineHeight: "5px",
                      marginTop: "10px",
                    }}
                  >
                    Error(s)
                  </p>
                  <p>
                    {markerData.status.errors.map((error, index) => {
                      return (
                        <React.Fragment key={`${markerData.id}-Error-${index}`}>
                          -{error}
                          <br />
                        </React.Fragment>
                      );
                    })}
                  </p>
                </span>
              </>
            )}
          </div>
          <div className="unit-link-box">
            <div
              className="unit-link"
              style={{
                top: "10px",
                left: "5px",
              }}
            >
              <img src={confluenceIcon} alt="" />
              <p>Create Work Item</p>
              <img
                src={openNewWindowIcon}
                alt=""
                style={{ right: "4px" }}
                onClick={() => {
                  openLink(markerData.links.createWorkItem);
                }}
              />
            </div>
            <div
              className="unit-link"
              style={{
                top: "60px",
                left: "5px",
              }}
            >
              <img src={zendeskIcon} alt="" />
              <p>Create Ticket</p>
              <img
                src={openNewWindowIcon}
                alt=""
                style={{ right: "4px" }}
                onClick={() => {
                  openLink(markerData.links.createTicket);
                }}
              />
            </div>
            <div
              className="unit-link"
              style={{
                top: "10px",
                left: "252px",
              }}
            >
              <img src={platformRadar} alt="" />
              <p>System Details</p>
              <img
                src={openNewWindowIcon}
                alt=""
                style={{ right: "4px" }}
                onClick={() => {
                  openLink(markerData.links.systemDetails);
                }}
              />
            </div>
            {/* <div className="unit-link" style={{
                            top: "60px",
                            left: "252px",
                        }}>
                            <img src={phoneIcon} alt="" />
                            <p>Contact Sys. Owner</p>
                            <img src={openNewWindowIcon} alt="" style={{ right: "4px" }} onClick={() => { openLink(markerData.links.sysOwnerContact) }} />
                        </div> */}
            <div className="line-divider"></div>
          </div>
        </>
      )}
    </div>
  );
}

export default ToolbarSystem;
